import { useCallback, useEffect, useState } from 'react';
import { Course } from '../../../types/course';
import { fetchCourseManifest } from '../data/courseClient';

interface UseCoursesResult {
  courses: Course[];
  loading: boolean;
  error: string | null;
  getCourseBySlug: (slug: string) => Course | undefined;
  refetch: () => Promise<void>;
}

export function useCourses(): UseCoursesResult {
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCourses = useCallback(async (bustCache = false) => {
    setLoading(true);
    setError(null);
    try {
      const manifest = await fetchCourseManifest(bustCache);
      setCourses(manifest.courses);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load courses';
      setError(message);
      setCourses([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);

  const getCourseBySlug = useCallback(
    (slug: string) => courses.find((c) => c.slug === slug),
    [courses]
  );

  return {
    courses,
    loading,
    error,
    getCourseBySlug,
    refetch: () => fetchCourses(true),
  };
}
